import { VariantReader } from "aldoo-ra/VariantReader"
import { twMerge } from "tailwind-merge"

/**
 * A spinner: a dim ring with a brighter arc turning around it.
 *
 * What makes it spin — the rotation, the dim track and the arc that stands out
 * against it — lives here rather than in the variants. A variant that only
 * says a colour (`primary`, `admin_primary`, …) used to leave the spinner
 * without an animation and with a track as solid as the arc, which reads as a
 * plain circle. Variants now only dress it: colour, size, thickness, speed.
 */
const DURATIONS = { slow: "2s", normal: "1s", fast: "0.5s" }

export default function Spinner({
  variant = "primary",
  size,
  thickness,
  speed = "normal",
  color,
  styleOverrides,
  className = "",
}) {
  // Get base variant styles
  const variantStyles = VariantReader.useVariant({
    componentName: "Spinner",
    variant,
    styleOverrides,
  })

  // Allow composition of variants. Each is read whether it was asked for or
  // not: React may keep one Spinner across renders whose props differ (a
  // table's own loading spinner giving way to the caller's), and a hook that
  // runs only when `size` is set then goes missing (React #300).
  const sizeVariant = VariantReader.useVariant({ componentName: "Spinner", variant: size || "" })
  const thicknessVariant = VariantReader.useVariant({ componentName: "Spinner", variant: thickness || "" })
  const colorVariant = VariantReader.useVariant({ componentName: "Spinner", variant: color || "" })
  const sizeStyles = size ? sizeVariant : ""
  const thicknessStyles = thickness ? thicknessVariant : ""
  const colorStyles = color ? colorVariant : ""

  // A size to fall back on, first so anything else wins: a variant that
  // carries its own (`minimal`, `button`, `loading`) keeps it, and so does a
  // `size` or a `className`.
  const combinedStyles = twMerge(
    "w-6 h-6",
    variantStyles,
    sizeStyles,
    thicknessStyles,
    colorStyles,
    className
  )

  return (
    <div className={combinedStyles}>
      <svg
        aria-hidden="true"
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full animate-spin"
        style={{ animationDuration: DURATIONS[speed] || DURATIONS.normal }}
      >
        {/* the ring it turns against: the same colour, faded */}
        <path
          className="track opacity-25"
          d="M 50,50 m 0,-45 a 45,45 0 1 1 -0.1,0"
          stroke="currentColor"
          strokeWidth="10"
          fill="none"
        />
        {/* the arc that shows the turning */}
        <path
          d="M 50,50 m 0,-45 a 45,45 0 0 1 45,45"
          stroke="currentColor"
          strokeWidth="10"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    </div>
  )
}
